import React, { useEffect, useMemo, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LightbulbIcon, TargetIcon, FlameIcon, LockIcon, SparklesIcon, TrophyIcon, BrainIcon } from 'lucide-react';
import { DjayAvatar, DjayEmotion } from './DjayAvatar';
import { DjayBubble } from './DjayBubble';
import { useDjaySelection } from './useDjaySelection';
import { useEasterEggs } from './useEasterEggs';
import { useVariantUnlocks } from './useVariantUnlocks';
import { createRapidTapDetector, createKonamiDetector, checkTimeBasedEgg, getRapidTapReaction } from './easterEggs';
import { useAppStore } from '../app/hooks/useAppStore';
import { useMotionBudget } from '../app/hooks/useMotionBudget';
import { useClickOutside } from '../app/hooks/useClickOutside';
import { soundManager } from '../utils/soundManager';
import { hapticManager } from '../utils/hapticManager';
import { dataLayer } from '../data/dataLayer';
import { MODULES } from '../content';
import { getDjayPersonalGreeting, getDjayViewMessage, getDjayDailyTip, getDjayIdleMessage } from './djayMessages';
import type { LearnerContext } from './djayMessages';
// ── Types ────────────────────────────────────────────────────
interface FloatingDjayProps {
  currentView?: string;
  onNavigate?: (path: string) => void;
}
interface PanelMenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  bgClass: string;
  hoverClass: string;
  textClass: string;
  onClick: () => void;
  /** Static display item (not a button) */
  isStatic?: boolean;
}
// ── Contextual Menu Builder ──────────────────────────────────
function getContextualMenuItems(currentView: string, learnerContext: LearnerContext, handlers: {
  onTipClick: () => void;
  onNavigate: (path: string) => void;
}): PanelMenuItem[] {
  const {
    streak
  } = learnerContext;
  const items: PanelMenuItem[] = [];
  // Always show "Weetje van de dag" (renamed from "Dagelijkse tip")
  items.push({
    id: 'weetje',
    label: 'Weetje van de dag',
    icon: <LightbulbIcon className="w-4 h-4" data-id="element-3321" />,
    bgClass: 'bg-blue-50',
    hoverClass: 'hover:bg-blue-100',
    textClass: 'text-blue-700',
    onClick: handlers.onTipClick
  });
  // Contextual items based on current view
  switch (currentView) {
    case 'home':
      items.push({
        id: 'oefenen',
        label: 'Ga oefenen',
        icon: <TargetIcon className="w-4 h-4" data-id="element-3322" />,
        bgClass: 'bg-orange-50',
        hoverClass: 'hover:bg-orange-100',
        textClass: 'text-orange-700',
        onClick: () => handlers.onNavigate('learningLab')
      });
      break;
    case 'subjects':
    case 'modules':
      items.push({
        id: 'voortgang',
        label: 'Bekijk voortgang',
        icon: <BrainIcon className="w-4 h-4" data-id="element-3323" />,
        bgClass: 'bg-emerald-50',
        hoverClass: 'hover:bg-emerald-100',
        textClass: 'text-emerald-700',
        onClick: () => handlers.onNavigate('progress')
      });
      break;
    case 'progress':
      items.push({
        id: 'oefenen',
        label: 'Ga oefenen',
        icon: <TargetIcon className="w-4 h-4" data-id="element-3324" />,
        bgClass: 'bg-orange-50',
        hoverClass: 'hover:bg-orange-100',
        textClass: 'text-orange-700',
        onClick: () => handlers.onNavigate('learningLab')
      });
      break;
    case 'collection':
      items.push({
        id: 'oefenen',
        label: 'Ga oefenen',
        icon: <TargetIcon className="w-4 h-4" data-id="element-3325" />,
        bgClass: 'bg-orange-50',
        hoverClass: 'hover:bg-orange-100',
        textClass: 'text-orange-700',
        onClick: () => handlers.onNavigate('learningLab')
      });
      break;
    case 'learningLab':
    case 'lab':
      items.push({
        id: 'collectie',
        label: 'Mijn collectie',
        icon: <TrophyIcon className="w-4 h-4" data-id="element-3326" />,
        bgClass: 'bg-purple-50',
        hoverClass: 'hover:bg-purple-100',
        textClass: 'text-purple-700',
        onClick: () => handlers.onNavigate('collection')
      });
      break;
    default:
      items.push({
        id: 'oefenen',
        label: 'Ga oefenen',
        icon: <TargetIcon className="w-4 h-4" data-id="element-3327" />,
        bgClass: 'bg-orange-50',
        hoverClass: 'hover:bg-orange-100',
        textClass: 'text-orange-700',
        onClick: () => handlers.onNavigate('learningLab')
      });
      break;
  }
  // Always show streak (tappable → navigate to progress)
  if (streak > 0) {
    items.push({
      id: 'streak',
      label: `Mijn streak: ${streak} dagen`,
      icon: <FlameIcon className="w-4 h-4 text-orange-500" data-id="element-3328" />,
      bgClass: 'bg-amber-50',
      hoverClass: 'hover:bg-amber-100',
      textClass: 'text-amber-700',
      onClick: () => handlers.onNavigate('progress')
    });
  }
  return items;
}
export function FloatingDjay({
  currentView,
  onNavigate
}: FloatingDjayProps) {
  const {
    state,
    activeAccount,
    isDemoMode,
    getModuleMasteryForGroup,
    getAnsweredQuestions
  } = useAppStore();
  const {
    shouldAnimate
  } = useMotionBudget();
  const {
    selectedVariant,
    setSelectedVariant
  } = useDjaySelection();
  const {
    triggerEgg,
    isDiscovered
  } = useEasterEggs();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isLongPressMenuOpen, setIsLongPressMenuOpen] = useState(false);
  const [hasDiscoveredLongPress, setHasDiscoveredLongPress] = useState(false);
  const [showEasterEggLabel, setShowEasterEggLabel] = useState(false);
  const [bubbleMessage, setBubbleMessage] = useState<string | null>(null);
  const [bubbleAutoDismiss, setBubbleAutoDismiss] = useState<number | undefined>(undefined);
  const [emotion, setEmotion] = useState<DjayEmotion>('happy');
  const [isPulsing, setIsPulsing] = useState(false);
  const [tapPulse, setTapPulse] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const pulseTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const isLongPressTriggered = useRef(false);
  const hasShownWelcomeRef = useRef(false);
  const welcomeTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const easterEggTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const lastViewRef = useRef<string | undefined>(currentView);
  const showProactiveMessageRef = useRef<(msg: string, newEmotion: DjayEmotion, duration: number) => void>(() => {});
  // Easter egg detectors (stable refs)
  const rapidTapRef = useRef(createRapidTapDetector(5, 2000, 1500));
  const konamiRef = useRef(createKonamiDetector());
  const timeEggShownRef = useRef(false);
  const streak = state?.streak?.currentTotalStreak || 0;
  const displayName = activeAccount?.displayName || 'vriend';
  // Build learner context for personalized messages
  const learnerContext = useMemo((): LearnerContext => {
    let masteredCount = 0;
    let totalQs = 0;
    MODULES.forEach(m => {
      totalQs += getAnsweredQuestions(m.id).length;
      if (getModuleMasteryForGroup(m.id) >= 85) masteredCount++;
    });
    return {
      displayName,
      streak,
      level: state?.stats?.level || 1,
      xp: state?.stats?.xp || 0,
      masteredCount,
      totalQuestionsAnswered: totalQs,
      dayOfWeek: new Date().getDay()
    };
  }, [displayName, streak, state?.stats?.level, state?.stats?.xp, getModuleMasteryForGroup, getAnsweredQuestions]);
  // Load easter egg state
  useEffect(() => {
    if (!activeAccount?.id) return;
    let cancelled = false;
    dataLayer.getDjayLongPressDiscovered(activeAccount.id).then(val => {
      if (!cancelled) setHasDiscoveredLongPress(val);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [activeAccount?.id]);
  // Use shared variant unlock hook
  const {
    variants: unlockedVariants
  } = useVariantUnlocks(hasDiscoveredLongPress);
  useClickOutside(panelRef, () => {
    if (isPanelOpen) setIsPanelOpen(false);
    if (isLongPressMenuOpen) setIsLongPressMenuOpen(false);
  });
  // Helper to show a proactive message with an attention pulse first
  const showProactiveMessage = (msg: string, newEmotion: DjayEmotion, duration: number) => {
    if (isPanelOpen || isLongPressMenuOpen) return;
    setIsPulsing(true);
    setEmotion(newEmotion);
    if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    pulseTimerRef.current = setTimeout(() => {
      setIsPulsing(false);
      setBubbleMessage(msg);
      setBubbleAutoDismiss(duration);
      soundManager.playDjaySpeak();
    }, 1000);
  };
  // Keep ref in sync with latest closure
  showProactiveMessageRef.current = showProactiveMessage;
  // 1. Welcome on first load — personalized greeting
  useEffect(() => {
    if (!hasShownWelcomeRef.current && activeAccount) {
      hasShownWelcomeRef.current = true;
      const greeting = getDjayPersonalGreeting(learnerContext);
      welcomeTimerRef.current = setTimeout(() => {
        showProactiveMessageRef.current(greeting.text, greeting.emotion, 4000);
      }, 1000);
    }
    return () => {
      if (welcomeTimerRef.current) clearTimeout(welcomeTimerRef.current);
    };
  }, [activeAccount, learnerContext]);
  // 2. View change reactions
  useEffect(() => {
    if (currentView && currentView !== lastViewRef.current && currentView !== 'practice') {
      lastViewRef.current = currentView;
      const viewMsg = getDjayViewMessage(currentView, {
        streak,
        displayName
      });
      showProactiveMessage(viewMsg.text, viewMsg.emotion, 3000);
    }
  }, [currentView, streak, displayName]);
  // 3. Idle detection (2 minutes) — personalized idle message
  useEffect(() => {
    const resetIdle = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        const idleMsg = getDjayIdleMessage(learnerContext);
        showProactiveMessageRef.current(idleMsg.text, idleMsg.emotion, 5000);
      }, 2 * 60 * 1000);
    };
    window.addEventListener('mousemove', resetIdle);
    window.addEventListener('keydown', resetIdle);
    window.addEventListener('touchstart', resetIdle);
    resetIdle();
    return () => {
      window.removeEventListener('mousemove', resetIdle);
      window.removeEventListener('keydown', resetIdle);
      window.removeEventListener('touchstart', resetIdle);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
      if (easterEggTimerRef.current) clearTimeout(easterEggTimerRef.current);
    };
  }, [learnerContext]);
  // Konami code listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (konamiRef.current.handleKey(e.key)) {
        const result = triggerEgg('konami-code');
        if (result.triggered && result.reaction) {
          setEmotion(result.reaction.emotion);
          setBubbleMessage(result.reaction.text);
          setBubbleAutoDismiss(5000);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [triggerEgg]);
  // Time-based Easter egg check (once per session)
  useEffect(() => {
    if (timeEggShownRef.current || !activeAccount) return;
    const timeEgg = checkTimeBasedEgg();
    if (timeEgg) {
      timeEggShownRef.current = true;
      // Show after welcome message (delay 3s)
      const timer = setTimeout(() => {
        const result = triggerEgg(timeEgg);
        if (result.triggered && result.reaction) {
          showProactiveMessageRef.current(result.reaction.text, result.reaction.emotion, 5000);
        }
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [activeAccount, triggerEgg]);
  const handlePointerDown = () => {
    isLongPressTriggered.current = false;
    longPressTimerRef.current = setTimeout(() => {
      isLongPressTriggered.current = true;
      setIsPanelOpen(false);
      setIsLongPressMenuOpen(true);
      // Easter egg logic — long press discovers DJ Goud
      if (!hasDiscoveredLongPress && activeAccount?.id) {
        setHasDiscoveredLongPress(true);
        dataLayer.saveDjayLongPressDiscovered(activeAccount.id, true);
        triggerEgg('long-press-goud'); // Track in Easter egg system
        setShowEasterEggLabel(true);
        if (easterEggTimerRef.current) clearTimeout(easterEggTimerRef.current);
        easterEggTimerRef.current = setTimeout(() => setShowEasterEggLabel(false), 4000);
        setEmotion('celebrating');
      } else {
        setEmotion('excited');
        soundManager.playDjaySpeak();
        hapticManager.hapticHeavy();
      }
    }, 500); // 500ms for long press
  };
  const handlePointerUp = () => {
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
  };
  const handleDjayClick = () => {
    if (isLongPressTriggered.current) return;
    setTapPulse(true);
    setTimeout(() => setTapPulse(false), 500);
    const tapResult = rapidTapRef.current.recordTap();
    if (tapResult.inCooldown) {
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
      return;
    }
    if (tapResult.triggered) {
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
      const reaction = getRapidTapReaction(tapResult.triggerCount);
      triggerEgg('rapid-tap');
      setIsPanelOpen(false);
      setIsLongPressMenuOpen(false);
      setEmotion(reaction.emotion);
      setBubbleMessage(reaction.text);
      setBubbleAutoDismiss(4000);
      setShowEasterEggLabel(false);
      soundManager.playDjaySpeak();
      hapticManager.hapticCelebration();
      return;
    }
    if (isLongPressMenuOpen) {
      setIsLongPressMenuOpen(false);
      return;
    }
    if (isPanelOpen) {
      setIsPanelOpen(false);
      return;
    }
    // Don't clear bubble — let proactive messages persist alongside panel
    setIsPulsing(false);
    if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
    const viewMsg = getDjayViewMessage(currentView || 'home', {
      streak,
      displayName
    });
    setEmotion(viewMsg.emotion);
    setIsPanelOpen(true);
    soundManager.playDjaySpeak();
  };
  const handleTipClick = () => {
    const tip = getDjayDailyTip();
    setEmotion(tip.emotion);
    setIsPanelOpen(false);
    setBubbleMessage(tip.text);
    setBubbleAutoDismiss(5000);
    soundManager.playDjaySpeak();
  };
  const handleNavClick = (path: string) => {
    setIsLongPressMenuOpen(false);
    setIsPanelOpen(false);
    if (onNavigate) onNavigate(path);
  };
  if (currentView === 'practice' || !activeAccount && !isDemoMode) {
    return null;
  }
  const greetingMsg = getDjayViewMessage(currentView || 'home', {
    streak,
    displayName
  });
  // Build dynamic menu items
  const menuItems = getContextualMenuItems(currentView || 'home', learnerContext, {
    onTipClick: handleTipClick,
    onNavigate: handleNavClick
  });
  return <motion.div className="fixed bottom-4 right-4 md:bottom-8 md:right-8 z-50 flex items-end justify-end pointer-events-none" initial={shouldAnimate ? {
    y: 100,
    opacity: 0
  } : false} animate={{
    y: 0,
    opacity: 1
  }} transition={{
    type: 'spring',
    damping: 15,
    stiffness: 200
  }} data-id="element-3329">
      <div className="relative flex items-end pointer-events-auto" ref={panelRef} data-id="element-3330">
        {/* Expanded Click Panel — Dynamic Menu */}
        <AnimatePresence data-id="element-3331">
          {isPanelOpen && !isLongPressMenuOpen && <motion.div initial={shouldAnimate ? {
          opacity: 0,
          y: 10,
          scale: 0.95
        } : false} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={shouldAnimate ? {
          opacity: 0,
          y: 10,
          scale: 0.95
        } : false} transition={{
          duration: 0.2
        }} className="absolute bottom-full right-0 mb-4 w-64 md:w-72 bg-white rounded-2xl shadow-xl border border-slate-100 p-4 origin-bottom-right" data-id="element-3332">
              <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white border-b border-r border-slate-100 transform rotate-45" data-id="element-3333" />

              <div className="relative z-10" data-id="element-3334">
                <p className="text-slate-800 font-medium mb-3 text-sm md:text-base" data-id="element-3335">
                  {greetingMsg.text}
                </p>

                <div className="space-y-2" data-id="element-3336">
                  {menuItems.map(item => <button key={item.id} onClick={item.onClick} className={`w-full flex items-center gap-2 px-3 py-2 ${item.bgClass} ${item.hoverClass} ${item.textClass} rounded-full text-sm font-medium transition-colors`} data-id="element-3337">
                      {item.icon}
                      {item.label}
                    </button>)}
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Long Press Quick Nav Menu */}
        <AnimatePresence data-id="element-3338">
          {isLongPressMenuOpen && <motion.div initial={shouldAnimate ? {
          opacity: 0,
          y: 10,
          scale: 0.95
        } : false} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={shouldAnimate ? {
          opacity: 0,
          y: 10,
          scale: 0.95
        } : false} transition={{
          duration: 0.15,
          ease: [0.32, 0.72, 0, 1]
        }} className="absolute bottom-full right-0 mb-4 w-[280px] md:w-[340px] bg-white rounded-2xl shadow-xl border border-slate-100 p-4 origin-bottom-right pointer-events-auto" data-id="element-3339">
              <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white border-b border-r border-slate-100 transform rotate-45" data-id="element-3340" />

              <div className="relative z-10" data-id="element-3341">
                <p className="text-xs text-slate-400 font-medium mb-3" data-id="element-3342">
                  Kies je stijl
                </p>

                {/* Gold Discovery Celebration */}
                <AnimatePresence data-id="element-3343">
                  {showEasterEggLabel && <motion.div initial={{
                opacity: 0,
                scale: 0.8,
                y: -10
              }} animate={{
                opacity: 1,
                scale: 1,
                y: 0
              }} exit={{
                opacity: 0,
                scale: 0.8,
                y: -10
              }} transition={{
                type: 'spring',
                damping: 15,
                stiffness: 200
              }} className="mb-4 relative overflow-hidden rounded-xl border-2 border-yellow-400 bg-gradient-to-r from-yellow-50 via-amber-50 to-yellow-50 p-4" data-id="element-3344">
                      {/* Shimmer overlay */}
                      <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-200/40 to-transparent" animate={{
                  x: ['-100%', '200%']
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'linear'
                }} data-id="element-3345" />
                      <div className="relative z-10 flex items-center gap-3" data-id="element-3346">
                        <motion.div animate={{
                    rotate: [0, 15, -15, 0],
                    scale: [1, 1.2, 1]
                  }} transition={{
                    duration: 0.6,
                    repeat: 2
                  }} data-id="element-3347">
                          <SparklesIcon className="w-8 h-8 text-yellow-500" data-id="element-3348" />
                        </motion.div>
                        <div data-id="element-3349">
                          <p className="font-black text-amber-900 text-base" data-id="element-3350">
                            🏆 Geheim ontdekt!
                          </p>
                          <p className="text-sm font-bold text-amber-700" data-id="element-3351">
                            DJ Goud is ontgrendeld!
                          </p>
                        </div>
                      </div>
                      {/* Floating sparkle particles */}
                      {[...Array(6)].map((_, i) => <motion.div key={i} className="absolute w-1.5 h-1.5 bg-yellow-400 rounded-full" style={{
                  left: `${15 + i * 15}%`,
                  top: `${20 + i % 3 * 25}%`
                }} animate={{
                  y: [0, -12, 0],
                  opacity: [0.3, 1, 0.3],
                  scale: [0.8, 1.2, 0.8]
                }} transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  delay: i * 0.2
                }} data-id="element-3352" />)}
                    </motion.div>}
                </AnimatePresence>

                <div className="grid grid-cols-4 gap-2" data-id="element-3353">
                  {unlockedVariants.map(v => {
                const isSelected = selectedVariant === v.id;
                const isNewGold = showEasterEggLabel && v.id === 'goud';
                return <button key={v.id} onClick={() => {
                  if (v.unlocked) {
                    setSelectedVariant(v.id);
                    setIsLongPressMenuOpen(false);
                  }
                }} disabled={!v.unlocked} className="flex flex-col items-center gap-1.5 group focus:outline-none" title={v.unlocked ? v.name : `${v.name} (Vergrendeld)`} aria-label={`Kies ${v.name} stijl`} data-id="element-3354">
                        <motion.div animate={isNewGold ? {
                    scale: [1, 1.15, 1]
                  } : {}} transition={isNewGold ? {
                    duration: 1,
                    repeat: Infinity
                  } : {}} className={`w-14 h-14 rounded-full flex items-center justify-center transition-all duration-200 relative
                            ${isNewGold ? 'ring-3 ring-yellow-400 bg-yellow-50 shadow-lg shadow-yellow-200/50' : ''}
                            ${isSelected && !isNewGold ? 'ring-2 ring-orange-400 scale-105 bg-orange-50 shadow-sm' : ''}
                            ${!isSelected && !isNewGold ? 'bg-slate-50 border border-slate-100 shadow-sm group-hover:bg-slate-100' : ''}
                            ${!v.unlocked ? 'opacity-50 grayscale cursor-not-allowed' : 'cursor-pointer'}
                          `} data-id="element-3355">
                          <div className="w-10 h-10" data-id="element-3356">
                            <DjayAvatar variant={v.id} emotion="happy" size="sm" data-id="element-3357" />
                          </div>
                          {!v.unlocked && <div className="absolute inset-0 bg-slate-900/5 rounded-full flex items-center justify-center backdrop-blur-[0.5px]" data-id="element-3358">
                              <LockIcon className="w-4 h-4 text-slate-500 drop-shadow-sm" data-id="element-3359" />
                            </div>}
                        </motion.div>
                        <span className={`text-[10px] font-medium transition-colors leading-tight text-center
                            ${isNewGold ? 'text-yellow-600 font-bold' : ''}
                            ${isSelected && !isNewGold ? 'text-orange-600' : ''}
                            ${!isSelected && !isNewGold ? 'text-slate-500' : ''}
                          `} data-id="element-3360">
                          {v.name}
                        </span>
                      </button>;
              })}
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Speech Bubble — now visible alongside panel for proactive messages */}
        <div className="absolute bottom-full right-0 mb-2 origin-bottom-right" data-id="element-3361">
          <DjayBubble message={bubbleMessage || ''} isVisible={!!bubbleMessage && !isLongPressMenuOpen && !showEasterEggLabel} onDismiss={() => setBubbleMessage(null)} autoDismissMs={bubbleAutoDismiss} position="bottom" data-id="element-3362" />
        </div>

        {/* Avatar Button */}
        <motion.button onPointerDown={handlePointerDown} onPointerUp={handlePointerUp} onPointerLeave={handlePointerUp} onClick={handleDjayClick} className="relative focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-300/50 rounded-full select-none" aria-label="Praat met Djay" whileHover={shouldAnimate ? {
        scale: 1.05
      } : undefined} whileTap={shouldAnimate ? {
        scale: 0.9,
        y: 4
      } : undefined} animate={shouldAnimate && !isPanelOpen && !isLongPressMenuOpen && !bubbleMessage ? {
        y: [0, -4, 0],
        transition: {
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut'
        }
      } : undefined} style={{
        willChange: 'transform',
        touchAction: 'none'
      }} data-id="element-3363">
          {/* Tap Pulse */}
          <AnimatePresence data-id="element-3364">
            {tapPulse && <motion.div initial={{
            opacity: 0.8,
            scale: 1
          }} animate={{
            opacity: 0,
            scale: 1.5
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.4,
            ease: 'easeOut'
          }} className="absolute inset-0 border-4 border-orange-300 rounded-full" data-id="element-3365" />}
          </AnimatePresence>

          {/* Attention Pulse Glow */}
          <AnimatePresence data-id="element-3366">
            {isPulsing && <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: [0.4, 0.8, 0.4],
            scale: [1, 1.2, 1]
          }} exit={{
            opacity: 0,
            scale: 0.8
          }} transition={{
            duration: 1.5,
            repeat: Infinity
          }} className="absolute inset-0 bg-orange-400 rounded-full blur-md" data-id="element-3367" />}
          </AnimatePresence>

          {/* Shadow */}
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3/4 h-2 bg-black/10 rounded-full blur-sm" data-id="element-3368" />

          <div className="w-12 h-12 md:w-16 md:h-16 relative z-10" data-id="element-3369">
            <DjayAvatar emotion={emotion} variant={selectedVariant} size="xl" className="w-full h-full drop-shadow-md" animate={shouldAnimate} data-id="element-3370" />
          </div>
        </motion.button>
      </div>
    </motion.div>;
}